/**
 * 来場者投票フェスサービス
 * 投票フェス機能の状態取得と投票送信を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';
import { buildVisitorImageUrl } from '../../../services/storage/visitorImageUrl';

/** 投票フェス画像バケット名 */
const VOTING_FEST_IMAGE_BUCKET = 'voting-images';

/** 状態取得 RPC 名 */
const VISITOR_GET_VOTING_FEST_STATUS_RPC_NAME = 'visitor_get_voting_fest_status';

/** 投票送信 RPC 名 */
const VISITOR_SUBMIT_VOTE_FEST_RPC_NAME = 'visitor_submit_vote_fest';

/** 入力不正エラーコード */
export const VISITOR_VOTING_FEST_INVALID_INPUT_CODE = 'VISITOR_VOTING_FEST_INVALID_INPUT';

/** セッション不正エラーコード */
export const VISITOR_VOTING_FEST_SESSION_INVALID_CODE = 'VISITOR_VOTING_FEST_SESSION_INVALID';

/** 取得失敗エラーコード */
export const VISITOR_VOTING_FEST_FAILED_CODE = 'VISITOR_VOTING_FEST_FAILED';

/** 機能 OFF エラーコード */
export const VISITOR_VOTING_FEST_DISABLED_CODE = 'VISITOR_VOTING_FEST_DISABLED';

/** 二重投票エラーコード */
export const VISITOR_VOTING_FEST_ALREADY_VOTED_CODE = 'VISITOR_VOTING_FEST_ALREADY_VOTED';

/**
 * RPC エラーに特定キーワードが含まれるか
 * @param {{ message?: string } | null} rpcError - RPC エラー
 * @param {string} keyword - 判定キーワード
 * @returns {boolean} 含まれる場合 true
 */
const includesRpcErrorMessage = (rpcError, keyword) => {
  if (!rpcError?.message) {
    return false;
  }
  return rpcError.message.includes(keyword);
};

/**
 * RPC エラーをアプリエラーコードへ変換する
 * @param {{ message?: string } | null} rpcError - RPC エラー
 * @returns {string} アプリ向けエラーコード
 */
const mapVotingFestRpcErrorCode = (rpcError) => {
  if (includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN') || includesRpcErrorMessage(rpcError, 'INVALID_CHOICE')) {
    return VISITOR_VOTING_FEST_INVALID_INPUT_CODE;
  }
  if (
    includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_VOTING_FEST_SESSION_INVALID_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'VOTING_DISABLED')) {
    return VISITOR_VOTING_FEST_DISABLED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'ALREADY_VOTED')) {
    return VISITOR_VOTING_FEST_ALREADY_VOTED_CODE;
  }
  return VISITOR_VOTING_FEST_FAILED_CODE;
};

/**
 * Storage パスから公開 URL を生成する
 * @param {any} supabaseClient - Supabase クライアント
 * @param {string} storagePath - ストレージパス
 * @returns {string} 公開 URL（無ければ空文字）
 */
const toVotingImageUrl = (supabaseClient, storagePath) => {
  return buildVisitorImageUrl(supabaseClient, VOTING_FEST_IMAGE_BUCKET, storagePath);
};

/**
 * RPC が返す 1 位候補キーを検証する
 * 来場者には票数を返さず、結果公開中の 1 位候補だけを受け取る
 * @param {unknown} value - RPC の winning_option
 * @returns {'a' | 'b' | 'c' | null} 1 位候補キー（未公開・不正値なら null）
 */
const normalizeWinningOption = (value) => {
  /** 正規化済みキー */
  const key = String(value ?? '').trim().toLowerCase();
  return key === 'a' || key === 'b' || key === 'c' ? key : null;
};

/**
 * RPC 戻り値の 1 行を検証して JS オブジェクト化する
 * @param {any} row - 行
 * @param {any} supabaseClient - Supabase クライアント
 * @returns {{
 *  isEnabled: boolean,
 *  hasVoted: boolean,
 *  showResults: boolean,
 *  contentVersion: string,
 *  questionText: string,
 *  options: Array<{ key: 'a' | 'b' | 'c', label: string, description: string, imagePath: string, imageUrl: string }>,
 *  winningOption: 'a' | 'b' | 'c' | null,
 * }} 状態オブジェクト
 */
const validateStatusRow = (row, supabaseClient) => {
  const isEnabled = Boolean(row?.is_enabled);
  const hasVoted = Boolean(row?.has_voted);
  const showResults = Boolean(row?.show_results);
  const contentVersion = String(row?.content_version ?? 'unversioned');
  const questionText = String(row?.question_text ?? '');
  const winningOption = normalizeWinningOption(row?.winning_option);

  /**
   * 1 オプションぶんを構築するヘルパ
   * @param {'a' | 'b' | 'c'} key - キー
   * @returns {{ key: string, label: string, description: string, imagePath: string, imageUrl: string }} オプション
   */
  const buildOption = (key) => {
    const label = String(row?.[`option_${key}_label`] ?? '');
    const description = String(row?.[`option_${key}_description`] ?? '');
    const imagePath = String(row?.[`option_${key}_image_path`] ?? '');
    const imageUrl = toVotingImageUrl(supabaseClient, imagePath);
    return { key, label, description, imagePath, imageUrl };
  };

  return {
    isEnabled,
    hasVoted,
    showResults,
    contentVersion,
    questionText,
    options: [buildOption('a'), buildOption('b'), buildOption('c')],
    winningOption,
  };
};

/**
 * 来場者の投票フェス状態を取得する
 * @param {string} sessionToken - セッショントークン
 * @returns {Promise<{
 *  isEnabled: boolean,
 *  hasVoted: boolean,
 *  showResults: boolean,
 *  contentVersion: string,
 *  questionText: string,
 *  options: Array<{ key: 'a' | 'b' | 'c', label: string, description: string, imagePath: string, imageUrl: string }>,
 *  winningOption: 'a' | 'b' | 'c' | null,
 * }>} 状態オブジェクト
 * @throws {AppError} 入力・通信エラー時
 */
export const visitorGetVotingFestStatus = async (sessionToken) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_VOTING_FEST_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabase クライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC 結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_GET_VOTING_FEST_STATUS_RPC_NAME, {
    p_session_token: sessionToken.trim(),
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapVotingFestRpcErrorCode(error);

    logError('visitor_get_voting_fest_status RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('投票フェス状態の取得に失敗しました。', {
      code: mappedErrorCode,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  /** RPC 戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;

  if (!firstRow) {
    return {
      isEnabled: false,
      hasVoted: false,
      showResults: false,
      contentVersion: 'unconfigured',
      questionText: '',
      options: [
        { key: 'a', label: '', description: '', imagePath: '', imageUrl: '' },
        { key: 'b', label: '', description: '', imagePath: '', imageUrl: '' },
        { key: 'c', label: '', description: '', imagePath: '', imageUrl: '' },
      ],
      winningOption: null,
    };
  }

  return validateStatusRow(firstRow, supabaseClient);
};

/**
 * 来場者の投票を送信する
 * @param {string} sessionToken - セッショントークン
 * @param {'a' | 'b' | 'c'} chosenOption - 投票先
 * @returns {Promise<{
 *  voteId: string,
 *  status: {
 *    isEnabled: boolean,
 *    hasVoted: boolean,
 *    showResults: boolean,
 *    contentVersion: string,
 *    questionText: string,
 *    options: Array<{ key: 'a' | 'b' | 'c', label: string, description: string, imagePath: string, imageUrl: string }>,
 *    winningOption: 'a' | 'b' | 'c' | null,
 *  },
 * }>} 登録後にDBで確定した投票状態と1位候補
 * @throws {AppError} 入力・状態・通信エラー時
 */
export const visitorSubmitVoteFest = async (sessionToken, chosenOption) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_VOTING_FEST_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** 正規化済み投票先 */
  const normalizedChoice = String(chosenOption ?? '').trim().toLowerCase();
  if (!['a', 'b', 'c'].includes(normalizedChoice)) {
    throw new AppError('投票先の指定が不正です。', {
      code: VISITOR_VOTING_FEST_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabase クライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC 結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_SUBMIT_VOTE_FEST_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_chosen_option: normalizedChoice,
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapVotingFestRpcErrorCode(error);

    logError('visitor_submit_vote_fest RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('投票の送信に失敗しました。', {
      code: mappedErrorCode,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  /** 新契約の先頭行（migration適用前のUUID文字列ならnull） */
  const firstRow = Array.isArray(data) ? data[0] : data;

  if (firstRow && typeof firstRow === 'object' && firstRow.vote_id) {
    return {
      voteId: String(firstRow.vote_id),
      status: validateStatusRow(firstRow, supabaseClient),
    };
  }

  /** 旧RPC稼働中もclient側加算せず、確定状態を再取得して整合させる */
  const confirmedStatus = await visitorGetVotingFestStatus(sessionToken);

  return {
    voteId: typeof data === 'string' ? data : String(data ?? ''),
    status: confirmedStatus,
  };
};

/** 強制表示告知取得 RPC 名（内部名は投票フェス発表会告知のまま） */
const VISITOR_GET_VOTING_FEST_ANNOUNCEMENT_RPC_NAME = 'visitor_get_voting_fest_announcement';

/** 告知リンクのボタン文言が未設定のときの既定値 */
export const VOTING_FEST_ANNOUNCEMENT_DEFAULT_LINK_LABEL = 'ホームページを見る';

/** 冒険メニューのカード名が未設定のときの既定値 */
export const VOTING_FEST_ANNOUNCEMENT_DEFAULT_MENU_LABEL = 'お知らせ';

/** 強制表示告知（発表会以外の告知にも使う） */
export type VotingFestAnnouncement = {
  isEnabled: boolean;
  title: string;
  body: string;
  imageUrl: string;
  linkUrl: string;
  linkLabel: string;
  menuLabel: string;
  contentVersion: string;
};

/** 非表示時の告知 */
const DISABLED_VOTING_FEST_ANNOUNCEMENT: VotingFestAnnouncement = {
  isEnabled: false,
  title: '',
  body: '',
  imageUrl: '',
  linkUrl: '',
  linkLabel: '',
  menuLabel: '',
  contentVersion: 'disabled',
};

/**
 * 告知リンクを https の URL だけに限定する
 * @param {unknown} value - RPC が返したリンク
 * @returns {string} 安全に開ける URL（不正なら空文字）
 */
const toSafeAnnouncementLinkUrl = (value) => {
  const rawUrl = String(value ?? '').trim();
  if (!rawUrl) {
    return '';
  }
  try {
    return new URL(rawUrl).protocol === 'https:' ? rawUrl : '';
  } catch {
    return '';
  }
};

/**
 * 来場者向けの強制表示告知を取得する
 * 投票の受付状態・投票済みかどうかに関係なく、ログイン中の来場者全員が対象です
 * @param {string} sessionToken - セッショントークン
 * @returns {Promise<VotingFestAnnouncement>} 告知（非表示中は isEnabled=false）
 * @throws {AppError} 入力・通信エラー時
 */
export const visitorGetVotingFestAnnouncement = async (sessionToken): Promise<VotingFestAnnouncement> => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_VOTING_FEST_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabase クライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC 結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_GET_VOTING_FEST_ANNOUNCEMENT_RPC_NAME, {
    p_session_token: sessionToken.trim(),
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapVotingFestRpcErrorCode(error);

    logError('visitor_get_voting_fest_announcement RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('お知らせの取得に失敗しました。', {
      code: mappedErrorCode,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  /** RPC 戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  /** 表示用タイトル */
  const title = String(firstRow?.title ?? '').trim();

  if (!firstRow?.is_enabled || !title) {
    return DISABLED_VOTING_FEST_ANNOUNCEMENT;
  }

  /** 表示用リンク */
  const linkUrl = toSafeAnnouncementLinkUrl(firstRow.link_url);

  return {
    isEnabled: true,
    title,
    body: String(firstRow.body ?? ''),
    imageUrl: toVotingImageUrl(supabaseClient, firstRow.image_path),
    linkUrl,
    linkLabel: linkUrl
      ? String(firstRow.link_label ?? '').trim() || VOTING_FEST_ANNOUNCEMENT_DEFAULT_LINK_LABEL
      : '',
    // カード名を返さない旧DBでも表示できるよう、空なら既定のカード名にする
    menuLabel: String(firstRow.menu_label ?? '').trim() || VOTING_FEST_ANNOUNCEMENT_DEFAULT_MENU_LABEL,
    contentVersion: String(firstRow.content_version ?? 'unversioned'),
  };
};
